// ============================================================================
// 1. 임포트 및 전역 설정 (Imports & Config)
// ============================================================================
import React, { useState, useMemo, useEffect, useRef } from 'react'; // 🌟 [수정] useMemo 추가
import { Contact, Deal, SalesRep } from '../types';
import { crmDataService } from '../services/crmDataService';
import { 
  FileSpreadsheet, Edit2, Calendar, LayoutList, Search,
  Star, TrendingUp, Info, CheckSquare, ChevronDown,
  Check, X, Plus, Trash2, Upload, // 🚨 [추가] Plus, Trash2 아이콘 추가 (추가/삭제용)
  ChevronLeft, ChevronRight, RefreshCw // 🚨 [추가] 연도 이동용 아이콘 추가
} from 'lucide-react';
import * as XLSX from 'xlsx';

import PermissionGate from './PermissionGate';



// 백엔드에서 오는 데이터 모양 정의
interface ForecastItem {
  id: string;
  구분: string;
  poNo: string;
  projNo: string;
  거래처: string;
  담당자: string;
  지역: string;
  제품금액: number;
  상품금액: number;
  소계: number;
  매출예정일: string;
  수금예정일: string;
  등급: string;
  건명: string;
  영업리드: string;
  산업분야: string;
  팀: string;
}

interface SalesForecastProps {
  // viewMode?: 'summary' | 'detail'; // ❌ [삭제: SalesAnalysis로 이동]
  deals: Deal[];
  contacts: Contact[];
  salesReps: SalesRep[];
  currentUser?: any;
  onUpdateContact: (contact: Contact) => void;
  onUpdateDeals: (deals: Deal[]) => void;

  // 🚨 [여기에 추가!] "숫자 형태의 신호탄을 받을 수 있다"고 선언합니다.
  globalRefreshKey?: number;
}

type ForecastCategory = '실적' | '확정' | '예정' | '미정' | '예산';
const LEAD_STAGE_OPTIONS = ['미방문', '방문', '견적', '제안서', '사양서', '조달접수', '계약'] as const;


// 👇 🚨 [에러 해결] 날짜를 안전하게 변환해주는 도우미 함수 추가
const parseDate = (value: string | null | undefined): Date | null => {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed;
};



const SalesForecast: React.FC<SalesForecastProps> = ({ 
  // viewMode = 'summary', // ❌ [삭제: SalesAnalysis로 이동]
  deals, 
  contacts, 
  salesReps, 
  currentUser,
  onUpdateContact, 
  onUpdateDeals,
  globalRefreshKey // 👈 🚨 [신규 추가]
}) => {
  // 🚨 [수정] 팀별 필터 삭제, 전체/개인만 남김
  const [filterType, setFilterType] = useState<'all' | 'individual'>('all');
  const [searchTerm, setSearchTerm] = useState<string>(''); // 🔍 거래처 검색어 상태
  
  // 🟢 [추가] DB에서 가져온 진짜 유저 목록을 담을 그릇
  const [dbUsers, setDbUsers] = useState<any[]>([]);
  const [selectedRep, setSelectedRep] = useState<string>(salesReps[0]?.name || '');
  const [activeTab, setActiveTab] = useState<ForecastCategory | '전체'>('전체');
  const [filterValue, setFilterValue] = useState<string>('');

  // 🚨 [수정] startDate, endDate 대신 선택된 연도와 다중 월을 관리합니다.
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [selectedMonths, setSelectedMonths] = useState<number[]>([]); // 빈 배열이면 '전체'
  
  // 🚨 API 호출용 날짜는 연도 전체로 자동 계산
  const startDate = `${selectedYear}-01-01`;
  const endDate = `${selectedYear}-12-31`;

  // 🚨 [추가] 페이지네이션 (10개, 50개, 100개 보기) 상태
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);
  const [currentPage, setCurrentPage] = useState<number>(1);

  const [isSyncing, setIsSyncing] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isFinalizing, setIsFinalizing] = useState(false);
  const [remoteForecastData, setRemoteForecastData] = useState<ForecastItem[]>([]);
  const effectiveForecastData = useMemo(() => {
    // 1. 부모가 준 deals 데이터가 유효하다면 그걸 우선적으로 사용합니다.
    if (deals && deals.length > 0) {
      return deals as any as ForecastItem[];
    }
    // 2. 부모 데이터가 아직 로딩 전이거나 없다면 직접 가져온 remoteForecastData를 씁니다.
    return remoteForecastData;
  }, [deals, remoteForecastData, globalRefreshKey]);

  

  // 👇 🚨 [추가] 편집 모드를 위한 상태 (수정 중인 ID와 입력값 임시 저장)
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Partial<ForecastItem>>({});

  // 👇 🚨 [추가] 모달 제어를 위한 상태 2줄 추가
  const [isReasonModalOpen, setIsReasonModalOpen] = useState(false);
  const [reasonText, setReasonText] = useState("");

  // 🟢 [추가] 매출 추가 모달 상태 및 데이터
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [newForecastItem, setNewForecastItem] = useState({
    project_name: '',     // 건명
    client_name: '',      // 거래처
    sales_amount_prod: 0, // 제품금액
    sales_amount_item: 0, // 상품금액
    sales_date: '',       // 매출예정일
    collection_date: '', // 수금예정일
    assignee: '미정',      // 담당자
    division: '예정',      // 구분
    team: '미배정',         // 팀
    lead_stage: '미방문'    //  영업리드
  });

  const colWidths: Record<string, number> = {
    category: 80, poNo: 80, projNo: 80, company: 140, rep: 80, region: 70, prod: 110, item: 110, subtotal: 120, date: 90, collectDate: 90, grade: 80, title: 180, lead: 110, industry: 110, action: 70 // 🟢 [수정] action 너비 확장, poNo/projNo 너비 확장
  };

  const handleExportExcel = () => {
    // 🚨 엑셀 내보낼 때도 필터링된 리스트(finalDisplayList)를 사용하도록 수정
    const listToExport = finalDisplayList; 

    if (!listToExport.length) {
      alert('내보낼 데이터가 없습니다.');
      return;
    }

    const ok = window.confirm(
      `현재 필터 기준으로 ${listToExport.length}건을 엑셀로 다운로드할까요?`
    );
    if (!ok) return; // 취소 누르면 중단

    const rows = listToExport.map(item => ({
      구분: item.구분,
      PO번호: item.poNo,
      Proj번호: item.projNo,
      거래처: item.거래처,
      담당자: item.담당자,
      지역: item.지역,
      제품금액: item.제품금액,
      상품금액: item.상품금액,
      소계: item.소계,
      매출예정일: item.매출예정일,
      수금예정일: item.수금예정일,
      등급: item.등급,
      건명: item.건명,
      영업리드: item.영업리드,
      산업분야: item.산업분야,
      팀: item.팀
    }));

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Forecast');
    XLSX.writeFile(wb, `forecast_${startDate}_${endDate}.xlsx`);
  };

  const handleUploadExcel = () => {
    setUploadFile(null);
    setIsUploadModalOpen(true);
  };

  const handleConfirmUpload = async () => {
    if (!uploadFile) {
      alert('엑셀 파일을 선택해주세요.');
      return;
    }

    const lower = uploadFile.name.toLowerCase();
    if (!(lower.endsWith('.xlsx') || lower.endsWith('.xls'))) {
      alert('엑셀 파일(.xlsx, .xls)만 업로드할 수 있습니다.');
      return;
    }

    setIsUploading(true); // 로딩 시작 (버튼 비활성화)
    
    // 1. GCS에 파일 업로드 실행
    const result = await crmDataService.uploadExcelFile('forecast', uploadFile);

    if (result?.status === 'success') {
      // 🚨 [핵심] 업로드 성공 직후, 백엔드에 DB 동기화(Refresh) 명령을 내립니다.
      // 세 번째 인자 'true'가 백엔드의 initialize_master_db를 깨우는 스위치입니다.
      const data = await crmDataService.getSalesForecastList(startDate, endDate, true);
      
      setRemoteForecastData(data || []);
      if (onUpdateDeals) onUpdateDeals(data || []);
      
      alert(`업로드 및 DB 반영이 완료되었습니다! (파일명: ${result.filename})`);
      setIsUploadModalOpen(false);
      setUploadFile(null);
    } else {
      alert(`업로드 실패: ${result?.message || '알 수 없는 오류'}`);
    }
    
    setIsUploading(false); // 로딩 종료
  };

  const handleFinalizeUpload = async () => {
    if (!window.confirm('가장 최신 버전 묶음을 병합해 최종본을 만들까요?')) return;
    setIsFinalizing(true);
    const result = await crmDataService.finalizeExcelUpload('forecast');
    setIsFinalizing(false);
    if (result?.status === 'success') {
      alert(`최종 업로드 완료: ${result.filename}`);

      // [추가] 최종 업로드 직후 forecast 동기화 + 화면 반영
      const data = await crmDataService.getSalesForecastList(startDate, endDate, true);
      setRemoteForecastData(data || []);
      if (onUpdateDeals) onUpdateDeals(data || []);
    } else {
      alert(`최종 업로드 실패: ${result?.message || '알 수 없는 오류'}`);
    }
  };

  const formatKRW = (val: number | undefined) => new Intl.NumberFormat('ko-KR').format(Math.round(val || 0));

  // 🚨 [수정] 백엔드 데이터(구분 텍스트)를 4가지 카테고리로 매핑
  const getCategoryFromItem = (categoryText: string): ForecastCategory => {
    if (!categoryText) return '미정';
    if (categoryText.includes('완료') || categoryText.includes('실적')) return '실적';
    if (categoryText.includes('확정') || categoryText.includes('계약')) return '확정';
    if (categoryText.includes('예정')) return '예정';
    if (categoryText.includes('예산')) return '예산'; // 🚨 [추가]
    return '미정';
  };


  // 👇 🚨 [추가] 수정 기능 관련 핸들러들 (Start, Cancel, Change, Save)
  
  // 1. 수정 모드 시작
  const startEdit = (item: ForecastItem) => {
    setEditingId(item.id);
    setEditForm({ ...item}); // 현재 값을 폼에 복사
  };

  // 2. 수정 취소
  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({});
  };

  // 3. 입력값 변경 핸들러 (소계 자동 계산 포함)
  const handleEditChange = (field: keyof ForecastItem, value: any) => {
    setEditForm(prev => {
      const updated = { ...prev, [field]: value };
      
      // 💰 제품/상품 금액 수정 시 소계 자동 계산
      if (field === '제품금액' || field === '상품금액') {
        const p = Number(updated.제품금액) || 0;
        const i = Number(updated.상품금액) || 0;
        updated.소계 = p + i;
      }
      return updated;
    });
  };

  // 4. 저장 (백엔드 전송)

  // 1. 저장 버튼 누르면 실행 (모달 열기)
  const handleSaveClick = () => {
    console.log("📢 [DEBUG] 모달 열기 버튼 눌림!"); 
    
    if (!editingId) return;
    setReasonText(""); // 텍스트 초기화
    setIsReasonModalOpen(true); // 모달 열기
  };

  // 2. 모달에서 [확인] 누르면 실행 (진짜 저장 및 API 호출)
  const handleConfirmSave = () => {  // 🚨 1. 여기 있던 async 글자를 지웠습니다.
    if (!editingId) return;

    // UI 먼저 갱신 (반응 속도 향상)
    const updatedList = remoteForecastData.map(item => 
      item.id === editingId ? { ...item, ...editForm } as ForecastItem : item
    );
    setRemoteForecastData(updatedList);
    
    // 🚨 2. [아주 중요] 영업기회(딜보드)도 바로 바뀌게 부모한테 바뀐 데이터를 던져줍니다.
    if (onUpdateDeals) onUpdateDeals(updatedList as any);

    // 🚨 3. 서버 응답을 기다리지 않고 여기서 모달을 바로 닫음
    setIsReasonModalOpen(false);
    setEditingId(null);
    setEditForm({});

    // 💡 여기서 현재 로그인한 유저의 이름을 가져옵니다. (없으면 기본값)
    const currentUserName = currentUser?.name || "123";
    
    // 🚨 4. 백엔드 전송 (await를 빼고 뒤에서 조용히 실행되게 만듦)
    crmDataService.updateForecastItem(editingId, editForm, reasonText, currentUserName)
      .then(success => {
        if (!success) {
          alert("저장에 실패했습니다.");
          // 실패 시 원복
          crmDataService.getSalesForecastList(startDate, endDate).then(data => {
            setRemoteForecastData(data || []);
            onUpdateDeals(data || []); // 원복할 때도 딜보드에 알려줌
          });
        }
      });
  };

  // 🟢 [추가] 매출 추가 핸들러
  const handleAddForecast = async () => {
    // 필수값 검증
    if (!newForecastItem.client_name || !newForecastItem.sales_date) {
    alert("거래처명과 매출예정일은 필수 입력 항목입니다."); // 메시지도 거래처명으로 변경!
    return;
  }

    try {
      // API 호출 (백엔드 구조에 맞게 변환)
      const payload = {
        project_name: newForecastItem.project_name || "",
        client_name: newForecastItem.client_name,
        sales_amount: Number(newForecastItem.sales_amount_prod) + Number(newForecastItem.sales_amount_item), // 소계
        sales_date: newForecastItem.sales_date,
        collection_date: newForecastItem.collection_date,
        status: newForecastItem.division,
        assignee: newForecastItem.assignee,
        team: newForecastItem.team,
        sales_amount_prod: Number(newForecastItem.sales_amount_prod),
        sales_amount_item: Number(newForecastItem.sales_amount_item)
        // 필요 시 다른 필드 추가
      };

      const success = await crmDataService.addForecastItem(payload); 

      if (success) {
        alert("매출 내역이 추가되었습니다.");
        setIsAddModalOpen(false);
        // 초기화
        setNewForecastItem({
          project_name: '', client_name: '', sales_amount_prod: 0, sales_amount_item: 0, 
          sales_date: '', collection_date: '-', assignee: '미정', division: '예정', team: '미배정', lead_stage: '미방문'
        });
        // 목록 새로고침
        const data = await crmDataService.getSalesForecastList(startDate, endDate);
        setRemoteForecastData(data || []);
      } else {
        alert("추가에 실패했습니다.");
      }
    } catch (e) {
      console.error(e);
      alert("오류가 발생했습니다.");
    }
  };

  // 🟢 [추가] 매출 삭제 핸들러
  const handleDeleteForecast = async (id: string) => {
    if (!window.confirm("정말로 이 매출 내역을 삭제하시겠습니까?\n삭제된 데이터는 복구할 수 없습니다.")) return;

    try {
      const success = await crmDataService.deleteForecastItem(id);
      if (success) {
        alert("삭제되었습니다.");
        // 목록 새로고침
        const data = await crmDataService.getSalesForecastList(startDate, endDate);
        setRemoteForecastData(data || []);
      } else {
        alert("삭제 실패");
      }
    } catch (e) {
      console.error(e);
      alert("삭제 중 오류가 발생했습니다.");
    }
  };

  // 👇 🚨 [추가] 월/분기 토글 함수
  const toggleMonth = (m: number) => {
    setSelectedMonths(prev => {
      const newMonths = prev.includes(m) ? prev.filter(x => x !== m) : [...prev, m];
      setCurrentPage(1); // 달 변경시 1페이지로 리셋
      return newMonths.sort((a, b) => a - b);
    });
  };

  const toggleQuarter = (q: number) => {
    const qMonths = { 1: [1,2,3], 2: [4,5,6], 3: [7,8,9], 4: [10,11,12] }[q] || [];
    const isAllActive = qMonths.every(m => selectedMonths.includes(m));
    
    setCurrentPage(1); // 1페이지로 초기화

    if (isAllActive) {
      // 다 켜져있으면 끄기!
      setSelectedMonths(prev => prev.filter(m => !qMonths.includes(m)));
    } else {
      // 아니면 켜기
      setSelectedMonths(prev => {
        const newSet = new Set([...prev, ...qMonths]);
        return Array.from(newSet).sort((a, b) => a - b);
      });
    }
  };

  // 🚨 [핵심 변경] 국내팀/영업팀 직원만 쏙 뽑아낸 엑기스 명단 (드롭다운 & 필터용)
  const domesticReps = useMemo(() => {
    if (!dbUsers || dbUsers.length === 0) return ['미정', '기타'];
    const reps = dbUsers.filter((u: any) => {
      const tName = u.team_name || u.team || '';
      return tName.includes('국내') || tName.includes('영업');
    }).map((u: any) => u.name);
    
    // 중복 제거 및 '미정', '기타' 추가
    return ['미정', ...Array.from(new Set(reps)), '기타'];
  }, [dbUsers]);

  // 1) 옵션용: 날짜만 필터 (드롭다운 옵션 생성용)
  const optionSource = useMemo(() => {
    return effectiveForecastData.filter(item => {
      const d = parseDate(item.매출예정일);
      if (!d) return false; // 날짜가 유효하지 않으면 패스
      
      const itemYear = d.getFullYear();
      const itemMonth = d.getMonth() + 1;
      
      return itemYear === selectedYear && (selectedMonths.length === 0 || selectedMonths.includes(itemMonth));
    });
  }, [effectiveForecastData, selectedYear, selectedMonths, globalRefreshKey]);

  // 2) 데이터용: 날짜 + 팀/개인 필터 (리스트/집계 공통)
  const filteredData = useMemo(() => {
    return effectiveForecastData.filter(item => {
      const d = parseDate(item.매출예정일);
      if (!d) return false;

      const itemYear = d.getFullYear();
      const itemMonth = d.getMonth() + 1;
      
      // 🚨 달력 startDate/endDate 대신 선택된 월(selectedMonths)로 필터 검사
      const inDate = itemYear === selectedYear && (selectedMonths.length === 0 || selectedMonths.includes(itemMonth));
      
      // 🚨 [수정] 팀 필터링 삭제, 개인 필터링만 유지
      const inFilter = filterType === 'all' ||
        (filterType === 'individual' && item.담당자 === filterValue);

      // 🔍 [수정/추가] 거래처명, 건명, 담당자 통합 검색 필터 적용!
      const searchLower = searchTerm.toLowerCase().trim();
      const matchesSearch = 
        item.거래처.toLowerCase().includes(searchLower) ||
        (item.건명 || '').toLowerCase().includes(searchLower) ||
        (item.담당자 || '').toLowerCase().includes(searchLower);

      return inDate && inFilter && matchesSearch;
    });
  }, [effectiveForecastData, selectedYear, selectedMonths, filterType, filterValue, searchTerm, globalRefreshKey]);
  

  const handleFullSync = async () => {
    setIsSyncing(true);
    try {
      const forecastSuccess = await crmDataService.syncForecastToSheet(deals, contacts);

      if (forecastSuccess /*&& detailSuccess*/) {
        alert('매출 내역이 구글 시트에 동기화되었습니다.');
      } else {
        alert('일부 데이터 동기화에 실패했습니다.');
      }
    } catch (error) {
      console.error("Sync error:", error);
      alert('동기화 중 오류가 발생했습니다.');
    } finally {
      setIsSyncing(false);
    }
  };

  //메인 리스트 데이터 필터링 (이제 forecastData 사용!)
  const filteredList = useMemo(() => {
    return filteredData.slice().sort((a, b) => 
      new Date(a.매출예정일).getTime() - new Date(b.매출예정일).getTime()
    );
  }, [filteredData]);

  // ----------------------------------------------------------------------------------
  // 🚨 [추가] 상단 통계 카드 데이터 계산 (하이브리드용)
  // ----------------------------------------------------------------------------------
  const stats = useMemo(() => {
    const init = { count: 0, amount: 0 };
    const summary = {
      전체: { ...init }, 실적: { ...init }, 확정: { ...init },
      예정: { ...init }, 미정: { ...init }, 예산: { ...init }
    };

    filteredList.forEach(item => {
      const cat = getCategoryFromItem(item.구분);
      const amt = item.소계 || 0;
      summary.전체.count++;
      summary.전체.amount += amt;
      if (summary[cat]) {
        summary[cat].count++;
        summary[cat].amount += amt;
      }
    });
    return summary;
  }, [filteredList]);

  // ----------------------------------------------------------------------------------
  // 🚨 [추가] 탭 클릭 시 실제 테이블에 보여질 데이터 (필터링 적용)
  // ----------------------------------------------------------------------------------
  const finalDisplayList = useMemo(() => {
    if (activeTab === '전체') return filteredList;
    return filteredList.filter(item => getCategoryFromItem(item.구분) === activeTab);
  }, [filteredList, activeTab]);

  // 👇 🚨 [신규] 페이지네이션 처리를 위해 리스트 자르기
  const totalPages = Math.max(1, Math.ceil(finalDisplayList.length / itemsPerPage));
  
  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(1);
  }, [finalDisplayList.length, itemsPerPage, totalPages, currentPage]);

  const paginatedList = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return finalDisplayList.slice(startIndex, startIndex + itemsPerPage);
  }, [finalDisplayList, currentPage, itemsPerPage]);


  // 필터 옵션 (드롭 다운) 표시
  const filterOptions = useMemo(() => {
    if (filterType === 'individual') {
      // 🚨 [수정] 위에서 뽑아놓은 국내팀+기타 명단을 그대로 필터 목록으로 제공!
      return domesticReps;
    }
    return [];
  }, [filterType, domesticReps]);

  useEffect(() => {
    let mounted = true;
    // 🟢 [수정] 날짜 필터링 백엔드 위임 (startDate, endDate 전달)
    crmDataService.getSalesForecastList(startDate, endDate, false, 'active') // 🚨 [추가] 'active' 타입으로 이탈 제외 데이터만 가져오기
      .then(data => { if (mounted) setRemoteForecastData(data || []); })
      .catch(() => { if (mounted) setRemoteForecastData([]); });

      // 🟢 3. [추가] 진짜 유저 목록 로드 (가짜 salesReps 무시)
    crmDataService.getAllUsers()
      .then(users => { if (mounted) setDbUsers(users); });

    return () => { mounted = false; };
  }, [startDate, endDate, globalRefreshKey]); // 👈 날짜 변경 시 재로딩

  useEffect(() => { setFilterValue(filterOptions[0] || ''); }, [filterType, filterOptions]);

 
  // 🚨 [복구] 리스트에서도 등급 색상이 필요해서 주석 해제함 (이거 없으면 에러나요!)
  const gradeColors: Record<string, string> = {
    'S': 'bg-purple-100 text-purple-700 border-purple-200',
    'A': 'bg-blue-100 text-blue-700 border-blue-200',
    'B': 'bg-green-100 text-green-700 border-green-200',
    'C': 'bg-cyan-100 text-cyan-700 border-cyan-200',
    'D': 'bg-slate-100 text-slate-500 border-slate-200'
  };

  return (
    // 🚀 [디자인 수술 1] gap을 줄여서 전체적으로 위로 끌어올림
    <div className="flex flex-col gap-4 pb-20 animate-fade-in relative">
      
      {/* 🚀 [디자인 수술 2] 중복 타이틀 삭제 & 동기화 버튼을 최상단 우측(-mt-[60px])으로 올려서 시간 라인과 맞춤 */}
      <div className="absolute right-0 -top-[60px] z-10 flex gap-2">
        <button 
          onClick={handleFullSync} 
          disabled={isSyncing}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-black shadow-md transition-all ${isSyncing ? 'bg-slate-200 text-slate-400 cursor-not-allowed' : 'bg-slate-900 text-white hover:bg-slate-800'}`}
        >
          {isSyncing ? <RefreshCw size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          {isSyncing ? '동기화 중...' : '구글 시트 동기화'}
        </button>
      </div>

      {/* 🚨 [수정] 기존 달력을 삭제하고 연도/월 다중 선택 버튼으로 변경 */}
      <div className="bg-white p-4 rounded-2xl shadow-sm border-2 border-slate-200 flex flex-col gap-4 mt-2">
        
        {/* 상단 1열: 연도 & 월 선택기 */}
        <div className="w-full flex flex-col xl:flex-row xl:items-center gap-4 bg-slate-50/70 p-3 rounded-xl border border-slate-200">
          
          <div className="flex items-center gap-3">
            {/* 연도 화살표 선택기 */}
            <div className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-lg border border-slate-300 shadow-sm">
              <button onClick={() => setSelectedYear(y => y - 1)} className="p-1 hover:bg-slate-100 rounded text-slate-500 transition-colors"><ChevronLeft size={16}/></button>
              <span className="font-black text-[15px] text-slate-800 w-16 text-center">{selectedYear}년</span>
              <button onClick={() => setSelectedYear(y => y + 1)} className="p-1 hover:bg-slate-100 rounded text-slate-500 transition-colors"><ChevronRight size={16}/></button>
            </div>

            {/* 분기 단축 버튼 */}
            <div className="flex items-center gap-1.5 border-l-2 border-slate-200 pl-3">
              {[1, 2, 3, 4].map(q => {
                const qMonths = { 1: [1,2,3], 2: [4,5,6], 3: [7,8,9], 4: [10,11,12] }[q] || [];
                const isActive = qMonths.every(m => selectedMonths.includes(m));
                return (
                  <button 
                    key={`q-${q}`} 
                    onClick={() => toggleQuarter(q)} 
                    className={`px-2.5 py-1.5 text-[11px] font-black rounded-lg transition-colors shadow-sm active:scale-95 ${isActive ? 'bg-indigo-600 text-white border-indigo-700' : 'text-indigo-600 bg-indigo-50 border border-indigo-100 hover:bg-indigo-100'}`}
                  >
                    {q}분기
                  </button>
                );
              })}
            </div>
          </div>

          {/* 1~12월 다중 토글 칩 */}
          <div className="flex flex-wrap items-center gap-1.5 xl:ml-auto">
            <button 
              onClick={() => { setSelectedMonths([]); setCurrentPage(1); }}
              className={`px-3 py-1.5 rounded-lg text-xs font-black border transition-all ${selectedMonths.length === 0 ? 'bg-slate-700 text-white border-slate-800 shadow-md' : 'bg-white text-slate-500 border-slate-200 hover:bg-slate-100'}`}
            >
              전체월
            </button>
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(m => {
              const isActive = selectedMonths.includes(m);
              return (
                <button
                  key={`m-${m}`}
                  onClick={() => toggleMonth(m)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-black border transition-all min-w-[42px] ${isActive ? 'bg-blue-600 text-white border-blue-700 shadow-md transform scale-105' : 'bg-white text-slate-500 border-slate-200 hover:bg-blue-50 hover:text-blue-600'}`}
                >
                  {m}월
                </button>
              );
            })}
          </div>
        </div>

        {/* 하단 2열: 검색 및 개인 필터 */}
        <div className="w-full flex flex-wrap items-center gap-4">
          <div className="bg-slate-100 p-1 rounded-xl flex border border-slate-200">
            {/* 🚨 [수정] 팀별 삭제, 전체/개인만 남김 */}
            {['all', 'individual'].map(f => (
              <button key={f} onClick={() => setFilterType(f as any)} className={`px-4 py-1.5 rounded-lg text-xs font-black transition-all ${filterType === f ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
                {f === 'all' ? '전체' : '개인'}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2 bg-blue-50/50 border-2 border-slate-300 px-3 py-1.5 rounded-2xl focus-within:border-blue-500 focus-within:bg-white transition-all shadow-sm">
          <Search size={16} className="text-blue-500" />
          <input 
            type="text" 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="거래처/건명/담당자 검색..." /* 🚨 텍스트 수정 */
            className="bg-transparent text-[13px] font-black outline-none w-[200px] text-slate-700 placeholder:text-blue-300"
          />
          {searchTerm && (
            <button onClick={() => setSearchTerm('')} className="text-slate-400 hover:text-slate-600">
              <X size={14} />
              </button>
            )}
            </div>

          

          {/* 조건부 드롭다운: 개인일 때만 나타남 */}
          {filterType !== 'all' && (
            <div className="relative animate-fade-in-right">
              <ChevronDown size={14} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
              <select 
                value={filterValue} 
                onChange={(e) => setFilterValue(e.target.value)}
                className="appearance-none pl-3 pr-8 py-1.5 text-xs font-bold bg-indigo-50 text-indigo-700 rounded-md border border-indigo-100 outline-none focus:ring-2 focus:ring-indigo-200 cursor-pointer min-w-[100px]"
              >
                {filterOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
              </select>
            </div>
          )}

          {/* 🟢 [수정] 매출 추가 버튼 생성 - PermissionGate user={currentUser} 로 명찰 변경 */}
          <PermissionGate user={currentUser} action="forecast_edit">
            <button
              onClick={() => setIsAddModalOpen(true)}
              className="flex items-center gap-2 text-[14px] font-black text-white bg-blue-600 px-4 py-2 rounded-xl hover:bg-blue-700 shadow-lg shadow-blue-200 transition-all active:scale-95 ml-2"
            >
              <Plus size={16} /> 매출 추가
            </button>
          </PermissionGate>

          <div className="ml-auto flex gap-2">
            <button
              onClick={handleUploadExcel}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-[12px] font-black text-blue-700 bg-blue-50/50 border border-slate-300 hover:bg-blue-100 transition-colors shadow-sm"
            >
              <Upload size={14} /> 파일 업로드
            </button>

            <button
              onClick={handleFinalizeUpload}
              disabled={isFinalizing}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-[12px] font-black text-orange-700 bg-orange-50/50 border border-slate-300 hover:bg-orange-100 transition-colors shadow-sm disabled:opacity-50"
            >
              <CheckSquare size={14} />
              {isFinalizing ? '최종 업로드 중...' : '최종 업로드'}
            </button>

            <button
              onClick={handleExportExcel}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-[12px] font-black text-emerald-700 bg-emerald-50/50 border border-slate-300 hover:bg-emerald-100 transition-colors shadow-sm"
            >
              <FileSpreadsheet size={14} /> 엑셀 다운로드
            </button>
          </div>
      </div>
      </div>

      {/* 📊 [NEW] 하이브리드 스타일 통계 요약 카드 (클릭 필터링 + 금액 호버) */}
      <div className="grid grid-cols-6 gap-3 mb-2">
        {Object.entries(stats).map(([label, data]) => {
          const isActive = activeTab === label;
          return (
            <div 
              key={label}
              onClick={() => { setActiveTab(label as any); setCurrentPage(1); }} // 🚨 탭 바꿀 때도 1페이지로
              className={`cursor-pointer p-3 rounded-2xl border-2 transition-all duration-200 flex flex-col items-center group
                ${isActive 
                  ? 'bg-blue-600 border-blue-600 shadow-lg shadow-blue-100' 
                  : 'bg-white border-slate-200 hover:border-blue-300 hover:shadow-sm'
                }`}
            >
              <span className={`text-[10px] font-black uppercase tracking-tighter mb-1 transition-colors
                ${isActive ? 'text-blue-100' : 'text-slate-500 group-hover:text-blue-600'}`}>
                {label}
              </span>
              <div className="flex items-baseline gap-0.5">
                <span className={`text-xl font-[1000] leading-none transition-colors
                  ${isActive ? 'text-white' : 'text-slate-800 group-hover:text-blue-600'}`}>
                  {data.count}
                </span>
                <span className={`text-[10px] font-bold 
                  ${isActive ? 'text-blue-200' : 'text-slate-400 group-hover:text-blue-400'}`}>건</span>
              </div>
              
              {/* 💰 금액: 평소엔 아주 연하게, 마우스 올리거나 활성화되면 선명하게 */}
              <span className={`text-[10px] font-black mt-1.5 transition-all duration-300
                ${isActive 
                  ? 'text-blue-100 opacity-100' 
                  : 'text-slate-400 opacity-70 group-hover:opacity-100 group-hover:text-slate-600'
                }`}>
                {/* 🚨 기존 33.3억 포맷 지우고 풀숫자로 포맷팅 */}
                {data.amount > 0 ? `₩${new Intl.NumberFormat('ko-KR').format(data.amount)}` : '-'}
              </span>
            </div>
          );
        })}
      </div>

      {/* 🚀 테이블 메인 컨테이너 */}
      <div className="bg-white rounded-[4px] shadow-xl border border-slate-200 overflow-hidden flex flex-col">
        
        {/* 🚨 [수정] 목록 개수 선택기를 표 상단으로 이동 */}
        <div className="flex items-center justify-end px-4 py-2 bg-slate-50 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold text-slate-500">목록 개수:</span>
            <select 
              value={itemsPerPage} 
              onChange={e => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }} 
              className="text-xs border border-slate-300 bg-white rounded-lg px-2 py-1 font-bold text-slate-700 outline-none focus:ring-2 focus:ring-blue-100 cursor-pointer"
            >
              <option value={10}>10개씩 보기</option>
              <option value={50}>50개씩 보기</option>
              <option value={100}>100개씩 보기</option>
            </select>
          </div>
        </div>

        <div className="overflow-x-auto custom-scrollbar">
          <table className="w-full text-left border-collapse table-fixed min-w-[1600px]">
            <thead>
              {/* 🚀 진한 남색 헤더 디자인 */}
              <tr className="bg-[#1e293b] text-slate-200 text-[11px] font-bold uppercase h-12 border-b border-slate-700">
                {Object.entries(colWidths).map(([key, width]) => (
                  <th
                    key={key}
                    style={{ width }}
                    className="px-4 relative text-center border-r border-slate-700/50 last:border-r-0"
                  >
                    {key === 'date' ? (
                      <span className="inline-flex flex-col leading-tight">
                        <span>매출예정일</span>
                        <span className="text-[9px] text-slate-400 font-normal">(매출일)</span>
                      </span>
                    ) : key === 'collectDate' ? (
                      <span className="inline-flex flex-col leading-tight">
                        <span>수금예정일</span>
                        <span className="text-[9px] text-slate-400 font-normal">(수금일)</span>
                      </span>
                    ) : key === 'category' ? '구분' : key === 'poNo' ? 'PO.No' : key === 'projNo' ? 'Proj.No' : key === 'company' ? '거래처' : key === 'rep' ? '담당자' : key === 'region' ? '지역' : key === 'prod' ? '제품금액' : key === 'item' ? '상품금액' : key === 'subtotal' ? '소계' : key === 'grade' ? '등급' : key === 'title' ? '건명' : key === 'lead' ? '영업리드' : key === 'industry' ? '산업분야' : '관리'}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-100 text-center">
              {/* 🚨 [적용] finalDisplayList 대신 paginatedList로 변경하여 페이지네이션 반영! */}
              {paginatedList.map((item, idx) => {
                const isEditing = editingId === item.id;
                
                // 구분별 배경색 매핑
                const categoryColorMap: Record<string, string> = {
                  '실적': 'bg-[#3b82f6]',
                  '확정': 'bg-[#10b981]',
                  '예정': 'bg-[#f59e0b]',
                  '미정': 'bg-[#cbd5e1]',
                  '예산': 'bg-[#cbd5e1]',
                };
                const currentCat = getCategoryFromItem(item.구분);
                const badgeColor = categoryColorMap[currentCat] || 'bg-slate-400';

                return (
                  <tr
                    key={item.id}
                    className={`text-[12px] h-12 transition-colors ${
                      isEditing ? 'bg-blue-50/50' : 'hover:bg-slate-50/80'
                    }`}
                  >
                    {/* 1. 구분 (상태 뱃지) */}
                    <td className="px-4 border-r border-slate-100">
                      {isEditing ? (
                        <select 
                          value={editForm.구분} 
                          onChange={e => handleEditChange('구분', e.target.value)} 
                          className="w-full border-2 border-indigo-300 rounded text-[11px] py-1 text-center bg-white font-bold"
                        >
                          {['실적', '확정', '예정', '미정', '예산'].map(o => <option key={o} value={o}>{o}</option>)}
                        </select>
                      ) : (
                        <span className={`text-[10px] font-black px-2 py-0.5 rounded-full shadow-sm text-white ${badgeColor}`}>
                          {currentCat}
                        </span>
                      )}
                    </td>

                    {/* 2. PO No */}
                    <td className="px-4 border-r border-slate-100">
                      {isEditing ? (
                        <input type="text" value={editForm.poNo || ''} onChange={e => handleEditChange('poNo', e.target.value)} className="w-full border-2 border-indigo-300 rounded text-[11px] text-center bg-white px-1 font-bold" />
                      ) : <span className="font-mono text-slate-400 truncate">{item.poNo}</span>}
                    </td>

                    {/* 3. Proj No */}
                    <td className="px-4 border-r border-slate-100">
                      {isEditing ? (
                        <input type="text" value={editForm.projNo || ''} onChange={e => handleEditChange('projNo', e.target.value)} className="w-full border-2 border-indigo-300 rounded text-[11px] text-center bg-white px-1 font-bold" />
                      ) : <span className="font-mono text-slate-400 truncate">{item.projNo}</span>}
                    </td>

                    {/* 4. 거래처 (강조색 + 🚨말줄임표) */}
                    <td className="px-4 border-r border-slate-100 bg-indigo-50/30 text-left truncate max-w-[140px]" title={item.거래처}>
                      {isEditing ? (
                        <input 
                          type="text" 
                          value={editForm.거래처 || ''} 
                          onChange={e => handleEditChange('거래처', e.target.value)} 
                          className="w-full border-2 border-indigo-300 rounded text-[11px] px-1 bg-white font-bold text-indigo-700 outline-none focus:ring-1 focus:ring-indigo-400" 
                          placeholder="거래처명 입력"
                        />
                      ) : (
                        <span className="font-bold text-indigo-600">{item.거래처}</span>
                      )}
                    </td>

                    {/* 5. 담당자 */}
                    <td className="px-4 text-center border-r border-slate-100">
                      {isEditing ? (
                        <select value={editForm.담당자} onChange={e => handleEditChange('담당자', e.target.value)} className="w-full border-2 border-indigo-300 rounded text-[11px] text-center bg-white font-bold">
                          {/* 🚨 기존 담당자가 드롭다운 명단에 없어도 사라지지 않게 포함시켜줌! */}
                          {Array.from(new Set([...domesticReps, editForm.담당자])).map(rep => (
                            <option key={rep} value={rep}>{rep}</option>
                          ))}
                        </select>
                      ) : <span className="font-black text-slate-800">{item.담당자}</span>}
                    </td>

                    {/* 6. 지역 */}
                    <td className="px-4 text-slate-500 border-r border-slate-100 text-center">{item.지역}</td>

                    {/* 7. 제품금액 (블루 톤) */}
                    <td className="px-4 text-right border-r border-slate-100 font-mono font-semibold text-blue-700">
                      {isEditing ? (
                        <input type="number" value={editForm.제품금액} onChange={e => handleEditChange('제품금액', e.target.value)} className="w-full text-right border-2 border-indigo-300 rounded bg-white px-1 font-bold" />
                      ) : formatKRW(item.제품금액)}
                    </td>

                    {/* 8. 상품금액 (블루 톤) */}
                    <td className="px-4 text-right border-r border-slate-100 font-mono font-semibold text-blue-700">
                      {isEditing ? (
                        <input type="number" value={editForm.상품금액} onChange={e => handleEditChange('상품금액', e.target.value)} className="w-full text-right border-2 border-indigo-300 rounded bg-white px-1 font-bold" />
                      ) : formatKRW(item.상품금액)}
                    </td>

                    {/* 9. 소계 (인디고 강조 배경) */}
                    <td className="px-4 text-right font-bold text-indigo-700 bg-indigo-50/50 border-r border-slate-100">
                      {isEditing ? formatKRW(editForm.소계) : formatKRW(item.소계)}
                    </td>

                    {/* 10. 매출예정일 */}
                    <td className="px-4 text-center border-r border-slate-100">
                      {isEditing ? (
                        <input type="date" value={editForm.매출예정일} onChange={e => handleEditChange('매출예정일', e.target.value)} className="w-full border-2 border-indigo-300 rounded text-[10px] bg-white font-bold" />
                      ) : <span className="font-mono text-slate-600">{item.매출예정일}</span>}
                    </td>

                    {/* 11. 수금예정일 */}
                    <td className="px-4 text-center border-r border-slate-100">
                      {isEditing ? (
                        <input type="date" value={editForm.수금예정일} onChange={e => handleEditChange('수금예정일', e.target.value)} className="w-full border-2 border-indigo-300 rounded text-[10px] bg-white font-bold"/>
                      ) : <span className="font-mono text-slate-600">{item.수금예정일}</span>}
                    </td>

                    {/* 12. 등급 */}
                    <td className="px-4 text-center border-r border-slate-100">
                      {isEditing ? (
                        <select value={editForm.등급} onChange={e => handleEditChange('등급', e.target.value)} className="w-full border-2 border-indigo-300 rounded text-center bg-white font-bold">
                          {['S','A','B','C','D'].map(g => <option key={g} value={g}>{g}</option>)}
                        </select>
                      ) : <span className={`px-2 py-0.5 rounded text-[10px] font-black border ${gradeColors[item.등급 || 'D']}`}>{item.등급}</span>}
                    </td>

                    {/* 13. 건명 (🚨말줄임표 추가) */}
                    <td className="px-4 border-r border-slate-100 text-left truncate max-w-[180px]" title={item.건명}>
                      {isEditing ? (
                        <input type="text" value={editForm.건명 || ''} onChange={e => handleEditChange('건명', e.target.value)} className="w-full border-2 border-indigo-300 rounded text-[11px] px-1 bg-white font-bold" />
                      ) : <span className="text-slate-600 font-medium">{item.건명}</span>}
                    </td>

                    {/* 14. 영업리드 */}
                    <td className="px-4 text-center border-r border-slate-100">
                      {isEditing ? (
                        <select value={editForm.영업리드} onChange={e => handleEditChange('영업리드', e.target.value)} className="w-full border-2 border-indigo-300 rounded text-[11px] py-1 text-center bg-white font-bold">
                          <option value="">선택</option>
                          {LEAD_STAGE_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                        </select>
                      ) : <span className="font-bold text-slate-700 text-[11px]">{item.영업리드}</span>}
                    </td>

                    {/* 15. 산업분야 */}
                    <td className="px-4 text-center border-r border-slate-100">
                      {isEditing ? (
                        <input type="text" value={editForm.산업분야 || ''} onChange={e => handleEditChange('산업분야', e.target.value)} className="w-full border-2 border-indigo-300 rounded text-[11px] px-1 text-center bg-white font-bold text-indigo-600" />
                      ) : <span className="text-indigo-500 font-bold text-[10px]">{item.산업분야}</span>}
                    </td>

                    {/* 16. 관리 버튼 - PermissionGate user={currentUser} 로 명찰 변경 */}
                    <td className="px-4 text-center">
                      {isEditing ? (
                        <div className="flex justify-center gap-1">
                          <button onClick={handleSaveClick} className="p-1 bg-blue-600 text-white rounded hover:bg-blue-700 shadow-md" title="저장"><Check size={14}/></button>
                          <button onClick={cancelEdit} className="p-1 bg-slate-200 text-slate-600 rounded hover:bg-slate-300" title="취소"><X size={14}/></button>
                        </div>
                      ) : (
                        <PermissionGate user={currentUser} action="forecast_edit">
                          <div className="flex justify-center gap-1">
                            <button onClick={() => startEdit(item)} className="p-1.5 text-slate-400 hover:text-indigo-600 transition-all" title="수정">
                              <Edit2 size={14}/>
                            </button>
                            <button onClick={() => handleDeleteForecast(item.id)} className="p-1.5 text-slate-400 hover:text-red-500 transition-all" title="삭제">
                              <Trash2 size={14}/>
                            </button>
                          </div>
                        </PermissionGate>
                      )}
                    </td>
                  </tr>
                );
              })}
              {paginatedList.length === 0 && (
                <tr><td colSpan={16} className="py-20 text-center text-slate-400 font-black">데이터가 없습니다.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* 🚨 [수정] 표 하단 페이지네이션 바 (페이지 이동만 남김) */}
        <div className="flex items-center justify-center px-6 py-3 bg-slate-50 border-t border-slate-200">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))} 
              disabled={currentPage === 1} 
              className="p-1.5 bg-white border border-slate-300 rounded-lg hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors shadow-sm"
            >
              <ChevronLeft size={16}/>
            </button>
            <span className="text-xs font-black text-slate-700">
              {currentPage} <span className="text-slate-400">/ {totalPages} 페이지</span>
            </span>
            <button 
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} 
              disabled={currentPage === totalPages} 
              className="p-1.5 bg-white border border-slate-300 rounded-lg hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors shadow-sm"
            >
              <ChevronRight size={16}/>
            </button>
          </div>
        </div>

      </div>
      {/* 👇 🚨 [여기가 수정 사유 모달] */}
      {isReasonModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 animate-fade-in">
          <div className="bg-white p-6 rounded-2xl shadow-2xl w-[400px] animate-scale-in">
            <h3 className="text-lg font-bold text-slate-800 mb-2">📝 변경 사유 입력</h3>
            <p className="text-xs text-slate-500 mb-4">데이터를 수정한 이유를 간단히 적어주세요. (필수)</p>
            
            <textarea 
              value={reasonText}
              onChange={(e) => setReasonText(e.target.value)}
              placeholder="예: 단가 조정으로 인한 금액 변경"
              className="w-full h-24 p-3 border border-slate-200 rounded-xl text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 resize-none transition-all"
              autoFocus
            />

            <div className="flex justify-end gap-2 mt-4">
              <button 
                onClick={() => setIsReasonModalOpen(false)}
                className="px-4 py-2 text-xs font-bold text-slate-500 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors"
              >
                취소
              </button>
              <button 
                onClick={handleConfirmSave} 
                className="px-4 py-2 text-xs font-bold text-white bg-blue-600 rounded-lg hover:bg-blue-700 shadow-lg shadow-blue-200 transition-all active:scale-95"
              >
                확인 및 저장
              </button>
            </div>
          </div>
        </div>
      )}

      {isUploadModalOpen && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center">
          <div className="w-[460px] bg-white rounded-2xl p-6 shadow-2xl">
            <h3 className="text-lg font-bold mb-4">엑셀 파일 업로드</h3>
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
              className="w-full text-sm text-slate-700 file:mr-3 file:px-3 file:py-1.5 file:rounded-lg file:border file:border-slate-300 file:bg-slate-50 file:font-bold"
            />
            <p className="text-xs text-slate-500 mt-2">엑셀 파일(.xlsx, .xls)만 업로드 가능합니다.</p>
            <div className="mt-6 flex justify-end gap-2">
              <button
                onClick={() => setIsUploadModalOpen(false)}
                disabled={isUploading}
                className="px-4 py-2 rounded-lg bg-slate-100 text-slate-700 text-sm font-bold hover:bg-slate-200 disabled:opacity-50"
              >
                취소
              </button>
              <button
                onClick={handleConfirmUpload}
                disabled={isUploading}
                className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-bold hover:bg-blue-700 disabled:opacity-50"
              >
                {isUploading ? '업로드 중...' : '추가하기'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 🆕 [매출 추가 모달] */}
      {isAddModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 animate-fade-in">
          <div className="bg-white p-6 rounded-2xl shadow-2xl w-[500px] animate-scale-in">
            <h3 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
              <Plus size={20} className="text-blue-600"/> 신규 매출 등록
            </h3>
            
            <div className="grid grid-cols-2 gap-4">
              
              {/* 1. 거래처명 */}
              <div className="space-y-1">
                <label className="text-[10px] font-bold text-slate-500">거래처명</label>
                <input className="w-full p-2 border rounded-lg text-xs font-bold" 
                  value={newForecastItem.client_name} 
                  onChange={e => setNewForecastItem({...newForecastItem, client_name: e.target.value})} 
                  placeholder="예: 삼성전자"
                />
              </div>

              {/* 2. 프로젝트명/건명 */}
              <div className="space-y-1">
                <label className="text-[10px] font-bold text-slate-500">프로젝트명 (건명)</label>
                <input className="w-full p-2 border rounded-lg text-xs font-bold" 
                  value={newForecastItem.project_name} 
                  onChange={e => setNewForecastItem({...newForecastItem, project_name: e.target.value})} 
                  placeholder="예: A사 서버 납품"
                />
              </div>
              <div className="space-y-1">
                <label className="text-[10px] font-bold text-slate-500">제품금액 (KRW)</label>
                <input type="number" className="w-full p-2 border rounded-lg text-xs font-bold text-right" 
                  value={newForecastItem.sales_amount_prod} 
                  onChange={e => setNewForecastItem({...newForecastItem, sales_amount_prod: Number(e.target.value)})} 
                />
              </div>
              <div className="space-y-1">
                <label className="text-[10px] font-bold text-slate-500">상품금액 (KRW)</label>
                <input type="number" className="w-full p-2 border rounded-lg text-xs font-bold text-right" 
                  value={newForecastItem.sales_amount_item} 
                  onChange={e => setNewForecastItem({...newForecastItem, sales_amount_item: Number(e.target.value)})} 
                />
              </div>
              <div className="space-y-1">
                <label className="text-[10px] font-bold text-slate-500">매출예정일</label>
                <input type="date" className="w-full p-2 border rounded-lg text-xs font-bold" 
                  value={newForecastItem.sales_date} 
                  onChange={e => setNewForecastItem({...newForecastItem, sales_date: e.target.value})} 
                />
              </div>
              <div className="space-y-1">
                <label className="text-[10px] font-bold text-slate-500">구분</label>
                <select className="w-full p-2 border rounded-lg text-xs font-bold"
                  value={newForecastItem.division}
                  onChange={e => setNewForecastItem({...newForecastItem, division: e.target.value})}
                >
                  {['예정', '확정', '실적', '미정', '예산'].map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>

              {/* 🚨 [수정] 담당자 선택 (국내팀 + 기존 담당자 + 기타) */}
              <div className="space-y-1">
                <label className="text-[10px] font-bold text-slate-500">담당자</label>
                <select 
                  className="w-full p-2 border rounded-lg text-xs font-bold" 
                  value={newForecastItem.assignee} 
                  onChange={e => {
                    const selectedName = e.target.value;
                    const matchedUser = dbUsers.find(u => u.name === selectedName);
                    // '기타'를 선택했을 때의 예외처리
                    const teamName = selectedName === '기타' ? '기타' : (matchedUser?.team_name || matchedUser?.team || '미배정');
                    setNewForecastItem({
                      ...newForecastItem, 
                      assignee: selectedName,
                      team: teamName
                    });
                  }} 
                >
                  {domesticReps.map(rep => (
                    <option key={rep} value={rep}>
                      {rep} 
                    </option>
                  ))}
                </select>
              </div>

              {/* 팀 (담당자 고르면 자동 입력됨 - 읽기 전용) */}
              <div className="space-y-1">
                <label className="text-[10px] font-bold text-slate-500">담당 부서 (자동)</label>
                <input 
                  className="w-full p-2 border rounded-lg text-xs font-bold bg-slate-100 text-slate-500 cursor-not-allowed"
                  value={newForecastItem.team}
                  readOnly 
                  title="담당자를 선택하면 자동으로 입력됩니다."
                />
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-2 border-t pt-4">
              <button onClick={() => setIsAddModalOpen(false)} className="px-4 py-2 text-xs font-bold text-slate-500 bg-slate-100 rounded-lg hover:bg-slate-200">취소</button>
              <button onClick={handleAddForecast} className="px-4 py-2 text-xs font-bold text-white bg-blue-600 rounded-lg shadow-md hover:bg-blue-700">등록하기</button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default SalesForecast;